/**
 * Utility to trim company ID prefix from invoice numbers for display
 * 
 * Backend stores full format: {companyId}-{series}-{yyMMdd}-{sequence}
 * Example (28 Oct 2025): 202500001-SINV-251028-0001
 * 
 * Frontend displays trimmed format: {prefix}{year}-{sequence}
 * Example: SINV-2025-0001
 */

/**
 * Removes company ID prefix from invoice number or reference number
 * @param {string} invoiceNumber - Full invoice or reference number with company ID
 * @param {string|number|null} [companyId] - Optional company ID to match specifically
 * @returns {string} - Invoice or reference number without company ID prefix
 * 
 * @example
 * trimInvoiceNumber('202500001-SINV-251028-0001') // Returns: 'SINV-251028-0001'
 * trimInvoiceNumber('REF-202600001-PINV-240101-0001') // Returns: 'REF-PINV-240101-0001'
 * trimInvoiceNumber('CN-REF-202600001-CN-240101-0001') // Returns: 'CN-REF-CN-240101-0001'
 * trimInvoiceNumber('SINV-251028-0001') // Returns: 'SINV-251028-0001' (already trimmed)
 * trimInvoiceNumber(null) // Returns: ''
 */
export function trimInvoiceNumber(invoiceNumber, companyId = null) {
  if (!invoiceNumber) return ''
  
  const str = String(invoiceNumber).trim()
  if (!str) return ''
  
  // If specific companyId is provided, remove it directly if present
  if (companyId) {
    const cidStr = String(companyId).trim()
    if (cidStr) {
      const cidRegex = new RegExp(`^((?:[A-Za-z]+-)*)${cidStr}-(.+)$`)
      const cidMatch = str.match(cidRegex)
      if (cidMatch) {
        const prefix = cidMatch[1]
        const rest = cidMatch[2]
        if (prefix && rest.startsWith(prefix)) {
          return rest
        }
        return `${prefix}${rest}`
      }
    }
  }

  // Format with prefix: e.g. REF-202600001-PINV-240101-0001 -> REF-PINV-240101-0001
  // e.g. SAL-202600001-SAL-261001-0001 -> SAL-261001-0001 (avoids SAL-SAL- duplication)
  const prefixedMatch = str.match(/^((?:[A-Za-z]+-)+)\d+-([A-Za-z].*)$/)
  if (prefixedMatch) {
    const prefix = prefixedMatch[1]
    const rest = prefixedMatch[2]
    if (rest.startsWith(prefix)) {
      return rest
    }
    return `${prefix}${rest}`
  }

  // Standard format without prefix: {companyId}-{rest}
  // Company ID is numeric, followed by a dash
  const match = str.match(/^\d+-(.+)$/)
  if (match) {
    return match[1]
  }
  
  // Already in old format or malformed, return as-is
  return str
}

/**
 * Trims company IDs from any invoice numbers embedded inside arbitrary text (e.g. narration)
 * @param {string} text - Text possibly containing invoice numbers with company IDs
 * @returns {string} - Text with company IDs removed from invoice numbers
 * 
 * @example
 * trimInvoiceNumbersInText('Inventory reduction: 202600001-SAL-261001-0001')
 * // Returns: 'Inventory reduction: SAL-261001-0001'
 */
export function trimInvoiceNumbersInText(text) {
  if (!text) return ''
  return String(text).replace(/\b((?:[A-Za-z]+-)+)?(\d{5,})-([A-Za-z][A-Za-z0-9]*(?:-[A-Za-z0-9]+)+)\b/g, (match, prefix, cid, invoice) => {
    if (!prefix) return invoice
    if (invoice.startsWith(prefix)) return invoice
    return `${prefix}${invoice}`
  })
}

/**
 * Alias for trimInvoiceNumber specifically for reference numbers
 */
export const trimReferenceNumber = trimInvoiceNumber

/**
 * Batch trim multiple invoice numbers
 * @param {Array} items - Array of objects with invoice number properties
 * @param {string} key - Property name containing invoice number
 * @returns {Array} - Array with trimmed invoice numbers
 */
export function trimInvoiceNumbers(items, key = 'invoiceNo') {
  if (!Array.isArray(items)) return items
  
  return items.map(item => ({
    ...item,
    [key]: trimInvoiceNumber(item[key])
  }))
}

/**
 * Get company ID from full invoice number
 * @param {string} invoiceNumber - Full invoice number with company ID
 * @returns {string|null} - Company ID or null if not found
 * 
 * @example
 * getCompanyIdFromInvoice('202500001-SINV-2025-0001') // Returns: '202500001'
 * getCompanyIdFromInvoice('REF-202600001-PINV-240101-0001') // Returns: '202600001'
 * getCompanyIdFromInvoice('SINV-2025-0001') // Returns: null
 */
export function getCompanyIdFromInvoice(invoiceNumber) {
  if (!invoiceNumber) return null
  
  const str = String(invoiceNumber).trim()
  const prefixed = str.match(/^((?:[A-Za-z]+-)+)(\d+)-[A-Za-z]/)
  if (prefixed) return prefixed[2]

  const match = str.match(/^(\d+)-/)
  return match ? match[1] : null
}

